import { useCallback } from 'react'

import { EditorPanelItem, ElementsPanelConfig } from '@editor/interfaces/editor'
import { InspectionItemType } from '@editor/interfaces/inspection'
import { isInspectionItem } from '@editor/services/InspectionSheet'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setSelectedElementIds } from '@editor/store/tool'
import { useSelector } from 'react-redux'

import { resetPerVolumeId, setHoveredGridPoint } from '../store'

const useElementsPanel = (): ElementsPanelConfig => {
  // Store
  const dispatch = useAppDispatch()
  const permissionSet = useSelector((state: RootState) => state.editor.permissionSet)
  const userType = useSelector((state: RootState) => state.user.userType)
  const workingGridPoints = useSelector((state: RootState) => state.toolGrid.workingGridPoints)
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const isLoading = useSelector((state: RootState) => state.toolGrid.isLoading)

  // Permission check
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  return {
    getPanelItems: useCallback(
      (parent?: EditorPanelItem['item']): EditorPanelItem[] => {
        // Grid falls under a volume
        if (!parent || !isInspectionItem(parent) || parent.item_type !== InspectionItemType.Volume) return []

        const grid = inspectionItems.find(
          (item) => item.item_type === InspectionItemType.Grid && item.volume_id === parent.inspection_item_id,
        )
        const workingGrid = workingGridPoints[parent.inspection_item_id!]
        if (grid && !workingGrid) {
          return [
            {
              key: `panel-grid-${grid.inspection_item_id!}`,
              name: 'グリッド点',
              item: grid,
              defaultCollapsed: true,
              isCountable: true,
              onDelete: isAllowedToModify,
              children: grid.grid?.list_distances?.map<EditorPanelItem>((distance, index) => ({
                key: `panel-grid-point-${grid.inspection_item_id}-${index}`,
                name: distance.name,
                toggleVisibility: true,
                onSelect: false,
                onHover(flag) {
                  dispatch(
                    setHoveredGridPoint(flag ? { gridId: grid.inspection_item_id!, pointIndex: index } : undefined),
                  )
                },
              })),
            },
          ]
        }

        if (workingGrid) {
          return [
            {
              key: `panel-grid-${parent.inspection_item_id!}`,
              name: 'グリッド点',
              defaultCollapsed: true,
              isCountable: true,
              toggleVisibility: true,
              isWorking: true,
              isSaving: isLoading,
              onSelect: false,
              onReset: isLoading
                ? undefined
                : () => {
                    dispatch(resetPerVolumeId(parent.inspection_item_id!))
                    dispatch(
                      setSelectedElementIds(
                        selectedElementIds.filter(
                          (id) =>
                            !id.includes(parent.inspection_item_id!) &&
                            !id.includes(grid?.inspection_item_id || '---') &&
                            !parent.shape_ids.polygons.includes(id),
                        ),
                      ),
                    )
                  },
              children: workingGrid.map<EditorPanelItem>((point, index) => ({
                key: `panel-grid-point-${parent.inspection_item_id}-${index}`,
                name: `t${index}`,
                toggleVisibility: true,
                onSelect: false,
              })),
            },
          ]
        }

        return []
      },
      [inspectionItems, isAllowedToModify, workingGridPoints, selectedElementIds, isLoading, dispatch],
    ),
  }
}

export default useElementsPanel
